import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MutableRefObject,
  type ReactNode,
} from 'react';

export interface BreadcrumbSegment {
  label?: ReactNode;
  trigger?: ReactNode;
  dropdownLabel?: string;
}

export interface PageBreadcrumbRegistration {
  items: Array<{ label: string; href?: string; isActive?: boolean }>;
  info?: ReactNode;
}

export type PageSurface = 'card' | 'flush';

export interface EditMenuBypass {
  icon: ReactNode;
  ariaLabel: string;
  onClick: () => void;
}

export interface PageChrome {
  extraSegments: Array<{ key: string; segment: BreadcrumbSegment }>;
  headerActions: Array<{ key: string; node: ReactNode }>;
  editMenuExtensions: Array<{ key: string; node: ReactNode }>;
  editMenuBypass: EditMenuBypass | null;
  breadcrumbEntries: Array<{ key: string; value: PageBreadcrumbRegistration }>;
  scrollRef: MutableRefObject<HTMLElement | null>;
  surface: PageSurface;
  pageLabelHref: string | null;
  detailSlot: HTMLElement | null;
  registerSegments(key: string, segments: BreadcrumbSegment[] | null): void;
  registerActions(key: string, actions: ReactNode | null): void;
  registerEditMenuExtension(key: string, node: ReactNode | null): void;
  registerEditMenuBypass(key: string, bypass: EditMenuBypass | null): void;
  registerBreadcrumb(key: string, breadcrumb: PageBreadcrumbRegistration | null): void;
  registerSurface(key: string, surface: PageSurface | null): void;
  registerPageLabelHref(key: string, href: string | null): void;
  setDetailSlot(el: HTMLElement | null): void;
}

const PageChromeContext = createContext<PageChrome | null>(null);

interface Entry<T> {
  key: string;
  value: T;
}

function upsertEntries<T>(list: Entry<T>[], key: string, value: T | null): Entry<T>[] {
  const idx = list.findIndex((e) => e.key === key);
  if (value === null) {
    if (idx === -1) return list;
    const next = list.slice();
    next.splice(idx, 1);
    return next;
  }
  if (idx === -1) return [...list, { key, value }];
  const existing = list[idx];
  if (existing && Object.is(existing.value, value)) return list;
  const next = list.slice();
  next[idx] = { key, value };
  return next;
}

export const PageChromeProvider = ({ children }: { children: ReactNode }) => {
  const [segmentEntries, setSegmentEntries] = useState<Entry<BreadcrumbSegment[]>[]>([]);
  const [actionEntries, setActionEntries] = useState<Entry<ReactNode>[]>([]);
  const [editMenuEntries, setEditMenuEntries] = useState<Entry<ReactNode>[]>([]);
  const [editMenuBypassEntries, setEditMenuBypassEntries] = useState<Entry<EditMenuBypass>[]>([]);
  const [breadcrumbEntries, setBreadcrumbEntries] = useState<Entry<PageBreadcrumbRegistration>[]>(
    []
  );
  const [surfaceEntries, setSurfaceEntries] = useState<Entry<PageSurface>[]>([]);
  const [pageLabelHrefEntries, setPageLabelHrefEntries] = useState<Entry<string>[]>([]);
  const [detailSlot, setDetailSlotState] = useState<HTMLElement | null>(null);
  const scrollRef = useRef<HTMLElement | null>(null);

  const registerSegments = useCallback((key: string, segments: BreadcrumbSegment[] | null) => {
    setSegmentEntries((prev) => upsertEntries(prev, key, segments));
  }, []);

  const registerActions = useCallback((key: string, actions: ReactNode | null) => {
    setActionEntries((prev) => upsertEntries(prev, key, actions));
  }, []);

  const registerEditMenuExtension = useCallback((key: string, node: ReactNode | null) => {
    setEditMenuEntries((prev) => upsertEntries(prev, key, node));
  }, []);

  const registerEditMenuBypass = useCallback((key: string, bypass: EditMenuBypass | null) => {
    setEditMenuBypassEntries((prev) => upsertEntries(prev, key, bypass));
  }, []);

  const registerBreadcrumb = useCallback(
    (key: string, breadcrumb: PageBreadcrumbRegistration | null) => {
      setBreadcrumbEntries((prev) => upsertEntries(prev, key, breadcrumb));
    },
    []
  );

  const registerSurface = useCallback((key: string, surface: PageSurface | null) => {
    setSurfaceEntries((prev) => upsertEntries(prev, key, surface));
  }, []);

  const registerPageLabelHref = useCallback((key: string, href: string | null) => {
    setPageLabelHrefEntries((prev) => upsertEntries(prev, key, href));
  }, []);

  const setDetailSlot = useCallback((el: HTMLElement | null) => {
    setDetailSlotState((prev) => (prev === el ? prev : el));
  }, []);

  const extraSegments = useMemo(
    () =>
      segmentEntries.flatMap(({ key, value }) =>
        value.map((segment, i) => ({ key: `${key}:${i}`, segment }))
      ),
    [segmentEntries]
  );

  const headerActions = useMemo(
    () => actionEntries.map(({ key, value }) => ({ key, node: value })),
    [actionEntries]
  );

  const editMenuExtensions = useMemo(
    () => editMenuEntries.map(({ key, value }) => ({ key, node: value })),
    [editMenuEntries]
  );

  const editMenuBypass = useMemo(() => {
    const last = editMenuBypassEntries.at(-1);
    return last ? last.value : null;
  }, [editMenuBypassEntries]);

  const surface = useMemo<PageSurface>(() => {
    const last = surfaceEntries.at(-1);
    return last ? last.value : 'card';
  }, [surfaceEntries]);

  const pageLabelHref = useMemo<string | null>(() => {
    const last = pageLabelHrefEntries.at(-1);
    return last ? last.value : null;
  }, [pageLabelHrefEntries]);

  const value = useMemo<PageChrome>(
    () => ({
      extraSegments,
      headerActions,
      editMenuExtensions,
      editMenuBypass,
      breadcrumbEntries,
      scrollRef,
      surface,
      pageLabelHref,
      detailSlot,
      registerSegments,
      registerActions,
      registerEditMenuExtension,
      registerEditMenuBypass,
      registerBreadcrumb,
      registerSurface,
      registerPageLabelHref,
      setDetailSlot,
    }),
    [
      extraSegments,
      headerActions,
      editMenuExtensions,
      editMenuBypass,
      breadcrumbEntries,
      surface,
      pageLabelHref,
      detailSlot,
      registerSegments,
      registerActions,
      registerEditMenuExtension,
      registerEditMenuBypass,
      registerBreadcrumb,
      registerSurface,
      registerPageLabelHref,
      setDetailSlot,
    ]
  );

  return <PageChromeContext.Provider value={value}>{children}</PageChromeContext.Provider>;
};

const FALLBACK_SCROLL_REF: MutableRefObject<HTMLElement | null> = { current: null };
const FALLBACK_CHROME: PageChrome = {
  extraSegments: [],
  headerActions: [],
  editMenuExtensions: [],
  editMenuBypass: null,
  breadcrumbEntries: [],
  scrollRef: FALLBACK_SCROLL_REF,
  surface: 'card',
  pageLabelHref: null,
  detailSlot: null,
  registerSegments: () => undefined,
  registerActions: () => undefined,
  registerEditMenuExtension: () => undefined,
  registerEditMenuBypass: () => undefined,
  registerBreadcrumb: () => undefined,
  registerSurface: () => undefined,
  registerPageLabelHref: () => undefined,
  setDetailSlot: () => undefined,
};

export const usePageChrome = (): PageChrome => {
  return useContext(PageChromeContext) ?? FALLBACK_CHROME;
};

interface RegisterOpts {
  key: string;
  segments?: BreadcrumbSegment[];
  actions?: ReactNode;
  editMenuExtension?: ReactNode;
  editMenuBypass?: EditMenuBypass | null;
  breadcrumb?: PageBreadcrumbRegistration;
  surface?: PageSurface;
  pageLabelHref?: string | null;
}

export const useRegisterPageChrome = ({
  key,
  segments,
  actions,
  editMenuExtension,
  editMenuBypass,
  breadcrumb,
  surface,
  pageLabelHref,
}: RegisterOpts): void => {
  const chrome = useContext(PageChromeContext);
  const chromeRef = useRef(chrome);
  chromeRef.current = chrome;

  useEffect(() => {
    const c = chromeRef.current;
    if (!c) return;
    if (segments !== undefined) c.registerSegments(key, segments);
    if (actions !== undefined) c.registerActions(key, actions);
    if (editMenuExtension !== undefined) c.registerEditMenuExtension(key, editMenuExtension);
    if (editMenuBypass !== undefined) c.registerEditMenuBypass(key, editMenuBypass);
    if (breadcrumb !== undefined) c.registerBreadcrumb(key, breadcrumb);
    if (surface !== undefined) c.registerSurface(key, surface);
    if (pageLabelHref !== undefined) c.registerPageLabelHref(key, pageLabelHref);
    return () => {
      const live = chromeRef.current;
      if (!live) return;
      if (segments !== undefined) live.registerSegments(key, null);
      if (actions !== undefined) live.registerActions(key, null);
      if (editMenuExtension !== undefined) live.registerEditMenuExtension(key, null);
      if (editMenuBypass !== undefined) live.registerEditMenuBypass(key, null);
      if (breadcrumb !== undefined) live.registerBreadcrumb(key, null);
      if (surface !== undefined) live.registerSurface(key, null);
      if (pageLabelHref !== undefined) live.registerPageLabelHref(key, null);
    };
  }, [
    key,
    segments,
    actions,
    editMenuExtension,
    editMenuBypass,
    breadcrumb,
    surface,
    pageLabelHref,
  ]);
};

export const useDetailSlot = (): HTMLElement | null => {
  const chrome = useContext(PageChromeContext);
  return chrome?.detailSlot ?? null;
};
